import FaIcon from '@/components/icon/FaIcon/FaIcon';
import formStyle from '@/styles/modules/form.module.scss';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form/dist/types';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormText.module.scss';

type Props<T extends FieldValues> = {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'decimal';
  label?: string;
  placeholder?: string;
  autocomplete?: string;
  required?: boolean;
  readOnly?: boolean;
  prefix?: React.ReactNode;
  onBlur?: () => void;
  isSmall?: boolean;
  maxLength?: number;
  dataTestId?: string;
  disabled?: boolean;
  fontSize?: 'default' | 'small';
} & UseControllerProps<T>;

function FormText<T extends FieldValues>({
  name,
  type = 'text',
  inputMode,
  label,
  placeholder,
  autocomplete = 'off',
  required = false,
  readOnly = false,
  control,
  prefix,
  onBlur,
  isSmall = false,
  maxLength,
  dataTestId,
  disabled = false,
  fontSize = 'default',
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control });

  const [showPassword, setShowPassword] = useState(false);

  const inputType = type === 'password' && showPassword ? 'text' : type;

  const fieldWrapperClass = label ? formStyle.fieldWrapper : formStyle.fieldWrapperNoneLabel;

  return (
    <div className={fontSize === 'small' ? formStyle.formComponentStyleSmall : formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && (
        <FormLabel>
          {label}
          {required && <span className={formStyle.required}>*</span>}
        </FormLabel>
      )}
      {/* フィールドブロック */}
      <div
        className={clsx(
          fieldWrapperClass,
          isSmall && styles.smallRect,
          type === 'password' && styles.passwordWrapper,
          disabled && formStyle.fieldWrapperDisabled,
        )}
      >
        {prefix}
        <input
          type={inputType}
          inputMode={inputMode}
          placeholder={placeholder}
          autoComplete={autocomplete}
          required={required}
          name={field.name}
          ref={field.ref}
          onBlur={() => {
            field.onBlur();
            if (onBlur) onBlur();
          }}
          onChange={field.onChange}
          value={field.value}
          className={styles.input}
          readOnly={readOnly}
          maxLength={maxLength}
          data-testid={dataTestId}
          disabled={disabled}
        />
        {type === 'password' && (
          <button
            type='button'
            className={styles.togglePassword}
            onClick={() => {
              setShowPassword(!showPassword);
            }}
            aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
          >
            <FaIcon icon={showPassword ? faEye : faEyeSlash} />
          </button>
        )}
      </div>
      {/* エラー */}
      {error && (
        <div className={formStyle.validateErrors}>
          <p>{(error as DeepMap<FieldValues, FieldError>).message}</p>
        </div>
      )}
    </div>
  );
}
export default FormText;
